import { Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { RouteParameterType } from '../../enums/route-parameters.enum';
import { RouteBuilderFactory } from '../route-builder/route-builder-factory.service';
import { RouteConfigurationFactory } from '../route-configuration/route-configuration-factory.service';
import { TemplateConfigurationFactory } from '../route-configuration/template-configuration-factory.service';
import { TransactionOverrideFactory } from '../transaction-override/transaction-override-factory.service';
import { BusinessTransactionService } from './business-transaction.service';
import { WIPStatusType } from '../../../wip/models/enums/w-i-p-status-type.enum';
import { DispatcherService } from '../dispatcher/dispatcher.service';
import { ActionTopic } from '../dispatcher/action-topic.enum';
import { SourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';

@Injectable()
export class RedirectTransactionService {

    constructor(private _router: Router,
        private routeBuilderFactory: RouteBuilderFactory,
        private templateConfigurationFactory: TemplateConfigurationFactory,
        private routeConfigurationFactory: RouteConfigurationFactory,
        private businessTransactionService: BusinessTransactionService,
        private transactionOverrideFactory: TransactionOverrideFactory,
        private dispatcherService: DispatcherService
    ) {

    }

    /**
     * Redirect to correct transaction route
     * @param businessTransactionNumberId
     */

    public async redirectToTransaction(businessTransactionNumberId: string, viewContainerRef?: ViewContainerRef) {

        if (businessTransactionNumberId != '') {
            let templateConfigurationService;

            // clear out any lingering validation messages from the transaction we're navigating away from
            this.dispatcherService.publish({ topic: ActionTopic.ValidationSuccess, values: null });

            // get the template configuration service
            templateConfigurationService = this.templateConfigurationFactory.create();

            // get the template configuration
            const templateConfiguration = templateConfigurationService.getTemplateConfiguration();

            let routeConfigurationService;
            // get the route configuration service
            routeConfigurationService = this.routeConfigurationFactory.create();

            // get the route configuration
            let businessTransactionMetadata = await this.businessTransactionService.getTransactionMetaData(businessTransactionNumberId).pipe(first()).toPromise();
            if (!businessTransactionMetadata || !businessTransactionMetadata.sourceSystemTypeId || businessTransactionMetadata.sourceSystemTypeId === 0) {
                console.log(businessTransactionMetadata);
            }

            // During the redirection, if the status of transaction is inacive, set it to active
            if (businessTransactionMetadata.wipStatusTypeId == new WIPStatusType().Inactive.id) {
                await this.businessTransactionService.activateInactiveTransaction(+businessTransactionMetadata.id).pipe(first()).toPromise();
            }

            if (viewContainerRef) {
                // let each subsystem determine if the transaction needs be changed based on other factors provided by the user after check-in.
                const overrideFactory = this.transactionOverrideFactory.create(+businessTransactionMetadata.sourceSystemTypeId, viewContainerRef);
                const newBusinessTranactionNumberId = await overrideFactory.getTransactionNumber(+businessTransactionNumberId, businessTransactionMetadata);

                if (+businessTransactionNumberId !== newBusinessTranactionNumberId) {
                    businessTransactionNumberId = '' + newBusinessTranactionNumberId;
                    businessTransactionMetadata = await this.businessTransactionService.getTransactionMetaData(businessTransactionNumberId).pipe(first()).toPromise();
                }
            }

            const defaultRouteConfigurationName = routeConfigurationService.getDefaultRouteConfiguration(businessTransactionMetadata);
            const routeConfiguration = routeConfigurationService.getRouteConfiguration(defaultRouteConfigurationName);

            // get the route params and defaults
            const params = (await routeConfigurationService.getRouteParameters(businessTransactionNumberId));

            const routeDefaults = routeConfigurationService.getRouteDefaults(params[RouteParameterType.sourceSystemTypeId], params[RouteParameterType.transactionTypeId], businessTransactionMetadata.wipStatusTypeId);

            if (+params[RouteParameterType.sourceSystemTypeId] == new SourceSystemType().Case.id) {
                this.finalizeCaseTransaction(params, businessTransactionNumberId);
            }

            // get the route builder and navigate to the transaction
            const routeBuilder = this.routeBuilderFactory.create(+params[RouteParameterType.sourceSystemTypeId]);
            const absoluteRoute = routeBuilder.getAbsoluteRoute(templateConfiguration, routeConfiguration, routeDefaults, params);

            this._router.navigate(absoluteRoute);
        }
    }

    /**
     * Calls the finalize endpoint for case transactions (to update the cart count to 0) since upon successful payment for these transactions,
     * the user is redirected back to the case instead of the spinner-layout component which handles calling the finalize endpoint for all other transactions.
     * @param params
     * @param businessTransactionNumberId
     */
    private finalizeCaseTransaction(params: any, businessTransactionNumberId: any) {
        if (+params[RouteParameterType.sourceSystemTypeId] == new SourceSystemType().Case.id) {
            this.businessTransactionService.getTransactionMetaData(businessTransactionNumberId).subscribe(
                r => {
                    var wipStatusTypeId = r.wipStatusTypeId;
                    let wipStatusType = new WIPStatusType();
                    if (+wipStatusTypeId === wipStatusType.PaymentComplete.id || +wipStatusTypeId === wipStatusType.AwaitingPostPayment.id) {
                        this.businessTransactionService.finalizeProcessingTransaction(+businessTransactionNumberId).pipe().subscribe();
                    }
                }
            );
        }
    }
}
